// src/automation/components/ReportDesignBlock.js
//
// Mapping-aware "Report Design" editor for SVR Inhouse. Lets an admin control
// the report presentation WITHOUT touching code:
//   • email text wrapper: greeting / intro / signoff (with {placeholders})
//   • TABLES (sections): group work types into one or more tables, each with a
//     title; reorder sections, reorder/rename work types within a section, move
//     a work type between sections. An "Unassigned" bucket shows mapping work
//     types not yet placed in any table.
//   • GLOBAL language column order (applies to every table)
//   • GLOBAL summary-row toggles (Total volume / Max Age / INSL / OOSL)
//
// Reads the queue mapping (getMap) to populate choices from REAL values and to
// validate. Saves a `report` block into the task config. An absent/empty block
// => backend falls back to current hardcoded behavior, so this is additive.

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  RefreshCw, Save, ArrowUp, ArrowDown, X, Plus, AlertTriangle, Info, Table2, ArrowRightLeft,
} from "lucide-react";
import { getMap } from "../api/automation_data";

const TOP_ROWS = [
  ["total_volume", "Total volume"],
  ["max_age", "Max Age"],
  ["insl", "INSL"],
  ["oosl", "OOSL"],
];
const PLACEHOLDERS = ["{date}", "{queue_count}", "{total_volume}", "{worktypes}"];
const isDefaultExcluded = (name) => String(name || "").toLowerCase().includes("exclud");

function uniqueSorted(values) {
  return [...new Set(values.filter((v) => v != null && String(v).trim() !== ""))].sort((a, b) =>
    String(a).localeCompare(String(b))
  );
}
function move(list, idx, dir) {
  const j = idx + dir;
  if (j < 0 || j >= list.length) return list;
  const copy = [...list];
  [copy[idx], copy[j]] = [copy[j], copy[idx]];
  return copy;
}

export default function ReportDesignBlock({ taskName, config, mapFile, canEdit, onSave }) {
  const { data: map, isLoading: mapLoading } = useQuery({
    queryKey: ["map", taskName, mapFile],
    queryFn: () => getMap(taskName, mapFile),
    enabled: !!taskName && !!mapFile,
    staleTime: 5 * 60 * 1000,
  });

  const { worktypesInMap, languagesInMap } = useMemo(() => {
    if (!map || !map.headers || !map.rows) return { worktypesInMap: [], languagesInMap: [] };
    const wtIdx = map.headers.findIndex((h) => String(h).trim().toLowerCase() === "work type");
    const langIdx = map.headers.findIndex((h) => String(h).trim().toLowerCase() === "language");
    const wts = wtIdx >= 0 ? map.rows.map((r) => String(r[wtIdx] ?? "").trim()) : [];
    const langs = langIdx >= 0 ? map.rows.map((r) => String(r[langIdx] ?? "").trim()) : [];
    return { worktypesInMap: uniqueSorted(wts), languagesInMap: uniqueSorted(langs) };
  }, [map]);

  const existing = config?.report || {};

  // Seed the section model. If the config already has sections use them; else if
  // it has a flat worktypes.order use that as a single section; else start with
  // one section titled from report.title / default containing nothing (the
  // "effective" fallback is computed below from the mapping).
  const [draft, setDraft] = useState(() => {
    const greeting = existing.greeting ?? "";
    const intro = existing.intro ?? "";
    const signoff = existing.signoff ?? "";
    const langOrder = Array.isArray(existing.languages?.order) ? existing.languages.order : [];
    const rows = { total_volume: true, max_age: true, insl: true, oosl: true, ...(existing.rows || {}) };

    let sections;
    if (Array.isArray(existing.sections) && existing.sections.length) {
      sections = existing.sections.map((s, i) => ({
        title: s.title || `Table ${i + 1}`,
        worktypes: Array.isArray(s.worktypes) ? [...s.worktypes] : [],
      }));
    } else if (Array.isArray(existing.worktypes?.order) && existing.worktypes.order.length) {
      sections = [{ title: existing.title || "SVR IN-HOUSE REPORT", worktypes: [...existing.worktypes.order] }];
    } else {
      sections = null; // means "use mapping default as one table" until edited
    }
    return { greeting, intro, signoff, langOrder, rows, sections };
  });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  // Effective sections: when the draft has none yet, present a single default
  // table containing all non-excluded mapping work types so the admin sees the
  // real current content to split up.
  const defaultWorktypes = worktypesInMap.filter((w) => !isDefaultExcluded(w));
  const sections = draft.sections ?? [
    { title: existing.title || "SVR IN-HOUSE REPORT", worktypes: defaultWorktypes },
  ];

  const assigned = new Set(sections.flatMap((s) => s.worktypes));
  const unassigned = defaultWorktypes.filter((w) => !assigned.has(w));
  // Work types referenced by a section but not present in the mapping.
  const unknownAssigned = [...assigned].filter((w) => !worktypesInMap.includes(w));

  const readOnly = !canEdit;

  // ── section mutators (always materialize draft.sections once touched) ──
  const setSections = (next) => setDraft((d) => ({ ...d, sections: typeof next === "function" ? next(d.sections ?? sections) : next }));

  const addSection = () =>
    setSections((secs) => [...(secs ?? sections), { title: `Table ${(secs ?? sections).length + 1}`, worktypes: [] }]);
  const removeSection = (si) =>
    setSections((secs) => (secs ?? sections).filter((_, i) => i !== si));
  const renameSection = (si, title) =>
    setSections((secs) => (secs ?? sections).map((s, i) => (i === si ? { ...s, title } : s)));
  const moveSection = (si, dir) => setSections((secs) => move(secs ?? sections, si, dir));

  const moveWorktype = (si, wi, dir) =>
    setSections((secs) =>
      (secs ?? sections).map((s, i) => (i === si ? { ...s, worktypes: move(s.worktypes, wi, dir) } : s))
    );
  const removeWorktype = (si, w) =>
    setSections((secs) =>
      (secs ?? sections).map((s, i) => (i === si ? { ...s, worktypes: s.worktypes.filter((x) => x !== w) } : s))
    );
  const addWorktypeTo = (si, w) =>
    setSections((secs) =>
      (secs ?? sections).map((s, i) =>
        i === si && !s.worktypes.includes(w) ? { ...s, worktypes: [...s.worktypes, w] } : s
      )
    );
  // Move a work type from its current section to another section index.
  const relocateWorktype = (w, toSi) =>
    setSections((secs) =>
      (secs ?? sections).map((s, i) => {
        const without = s.worktypes.filter((x) => x !== w);
        if (i === toSi) return { ...s, worktypes: [...without, w] };
        return { ...s, worktypes: without };
      })
    );

  const setLangOrder = (next) => setDraft((d) => ({ ...d, langOrder: next }));
  const addLang = (l) => !draft.langOrder.includes(l) && setLangOrder([...draft.langOrder, l]);
  const removeLang = (l) => setLangOrder(draft.langOrder.filter((x) => x !== l));

  // Build the `report` block, omitting empty pieces (so backend defaults apply).
  const buildReportBlock = () => {
    const report = {};
    if (draft.greeting.trim()) report.greeting = draft.greeting.trim();
    if (draft.intro.trim()) report.intro = draft.intro.trim();
    if (draft.signoff.trim()) report.signoff = draft.signoff.trim();

    if (draft.langOrder.length) report.languages = { order: draft.langOrder };

    const rowsChanged = TOP_ROWS.some(([k]) => draft.rows[k] === false);
    if (rowsChanged) report.rows = { ...draft.rows };

    // Only persist sections if the admin actually created a grouping (more than
    // one table, OR a single table that isn't just "everything"). A single table
    // containing all default work types is left implicit so it stays adaptive.
    const effective = sections.filter((s) => s.worktypes.length);
    const isImplicitSingle =
      effective.length === 1 &&
      effective[0].worktypes.length === defaultWorktypes.length &&
      defaultWorktypes.every((w) => effective[0].worktypes.includes(w));
    if (effective.length && !isImplicitSingle) {
      report.sections = effective.map((s) => ({ title: s.title, worktypes: s.worktypes }));
    } else if (effective.length === 1 && isImplicitSingle && effective[0].title) {
      // Single implicit table but with a custom title -> keep the title only.
      if (effective[0].title !== "SVR IN-HOUSE REPORT") report.title = effective[0].title;
    }
    return report;
  };

  const handleSave = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const report = buildReportBlock();
      const next = { ...config };
      if (Object.keys(report).length) next.report = report;
      else delete next.report;
      await onSave(next);
      setNotice({ type: "ok", msg: "Report design saved. Run a Test to preview the email." });
    } catch (e) {
      setNotice({ type: "err", msg: e?.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDraft({
      greeting: "", intro: "", signoff: "", langOrder: [],
      rows: { total_volume: true, max_age: true, insl: true, oosl: true },
      sections: null,
    });
    setNotice({ type: "ok", msg: "Reverted to a single default table (not saved yet). Click Save to apply." });
  };

  return (
    <div className="auto-rd">
      {mapLoading && (
        <div className="auto-eb-alert">
          <span>Loading mapping… work-type and language options will appear shortly.</span>
        </div>
      )}
      {!mapLoading && worktypesInMap.length === 0 && (
        <div className="auto-eb-alert auto-eb-alert--err">
          <AlertTriangle size={13} />
          <span>
            Couldn't read work types from the mapping ({mapFile}). You can still edit text and
            save, but table/work-type pickers are empty. Check the mapping file has a "Work type" column.
          </span>
        </div>
      )}
      {notice && (
        <div className={`auto-eb-alert ${notice.type === "err" ? "auto-eb-alert--err" : "auto-eb-alert--ok"}`}>
          <span>{notice.msg}</span>
        </div>
      )}

      {/* ── Email text ── */}
      <section className="auto-rd-sec">
        <div className="auto-rd-sechead">
          <h5 className="auto-rd-h">Email text</h5>
          <span className="auto-rd-hint">Blank = default · placeholders: {PLACEHOLDERS.join(" ")}</span>
        </div>
        <div className="auto-rd-grid2">
          <label className="auto-rd-field">
            <span>Greeting</span>
            <input className="auto-eb-input" disabled={readOnly} value={draft.greeting}
              placeholder="Hello Team," onChange={(e) => setDraft({ ...draft, greeting: e.target.value })} />
          </label>
          <label className="auto-rd-field">
            <span>Sign-off</span>
            <input className="auto-eb-input" disabled={readOnly} value={draft.signoff}
              placeholder="Regards,<br>GCC Automation" onChange={(e) => setDraft({ ...draft, signoff: e.target.value })} />
          </label>
          <label className="auto-rd-field auto-rd-field--full">
            <span>Intro line</span>
            <input className="auto-eb-input" disabled={readOnly} value={draft.intro}
              placeholder="Please find below SVR In-House volume…" onChange={(e) => setDraft({ ...draft, intro: e.target.value })} />
          </label>
        </div>
      </section>

      {/* ── Tables (sections) ── */}
      <section className="auto-rd-sec">
        <div className="auto-rd-sechead">
          <h5 className="auto-rd-h">Tables</h5>
          <span className="auto-rd-hint">
            {sections.length} table{sections.length === 1 ? "" : "s"} · group work types; one table or many
          </span>
          {!readOnly && (
            <button className="auto-rd-chip" onClick={addSection}><Plus size={11} /> Add table</button>
          )}
        </div>

        {unknownAssigned.length > 0 && (
          <div className="auto-eb-alert auto-eb-alert--err">
            <AlertTriangle size={13} />
            <span>Not in mapping (skipped): {unknownAssigned.join(", ")}</span>
          </div>
        )}

        {sections.map((sec, si) => (
          <div className="auto-rd-table" key={si}>
            <div className="auto-rd-table-head">
              <Table2 size={14} className="auto-rd-table-ic" />
              <input
                className="auto-eb-input auto-rd-title-in"
                disabled={readOnly}
                value={sec.title}
                placeholder={`Table ${si + 1} title`}
                onChange={(e) => renameSection(si, e.target.value)}
              />
              <span className="auto-rd-count">{sec.worktypes.length}</span>
              {!readOnly && (
                <span className="auto-rd-actions">
                  <button className="auto-rd-ic" title="Table up" disabled={si === 0}
                    onClick={() => moveSection(si, -1)}><ArrowUp size={12} /></button>
                  <button className="auto-rd-ic" title="Table down" disabled={si === sections.length - 1}
                    onClick={() => moveSection(si, 1)}><ArrowDown size={12} /></button>
                  <button className="auto-rd-ic auto-rd-ic--del" title="Remove table" disabled={sections.length === 1}
                    onClick={() => removeSection(si)}><X size={12} /></button>
                </span>
              )}
            </div>

            {sec.worktypes.length === 0 ? (
              <div className="auto-rd-empty-table">No work types yet — add from the pool below.</div>
            ) : (
              <div className="auto-rd-cards">
                {sec.worktypes.map((w, wi) => {
                  const inMap = worktypesInMap.includes(w);
                  return (
                    <div key={w} className={`auto-rd-card ${inMap ? "" : "auto-rd-card--bad"}`}>
                      <div className="auto-rd-card-top">
                        <span className="auto-rd-ord">{wi + 1}</span>
                        <span className="auto-rd-name" title={w}>{w}{!inMap && " ⚠"}</span>
                        {!readOnly && (
                          <span className="auto-rd-actions">
                            <button className="auto-rd-ic" title="Up" disabled={wi === 0}
                              onClick={() => moveWorktype(si, wi, -1)}><ArrowUp size={11} /></button>
                            <button className="auto-rd-ic" title="Down" disabled={wi === sec.worktypes.length - 1}
                              onClick={() => moveWorktype(si, wi, 1)}><ArrowDown size={11} /></button>
                            <button className="auto-rd-ic auto-rd-ic--del" title="Remove from table"
                              onClick={() => removeWorktype(si, w)}><X size={11} /></button>
                          </span>
                        )}
                      </div>
                      {!readOnly && sections.length > 1 && (
                        <div className="auto-rd-move">
                          <ArrowRightLeft size={10} />
                          <select
                            className="auto-rd-move-sel"
                            value={si}
                            onChange={(e) => relocateWorktype(w, Number(e.target.value))}
                          >
                            {sections.map((s2, j) => (
                              <option key={j} value={j}>{s2.title || `Table ${j + 1}`}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* add from unassigned pool directly into THIS table */}
            {!readOnly && unassigned.length > 0 && (
              <div className="auto-rd-add">
                <span>Add:</span>
                {unassigned.map((w) => (
                  <button key={w} className="auto-rd-chip" onClick={() => addWorktypeTo(si, w)}>
                    <Plus size={11} /> {w}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        {unassigned.length > 0 && (
          <div className="auto-rd-warn">
            <Info size={13} />
            <span>{unassigned.length} work type(s) not in any table: {unassigned.join(", ")}</span>
          </div>
        )}
      </section>

      {/* ── Global language order + summary rows ── */}
      <div className="auto-rd-row2">
        <section className="auto-rd-sec auto-rd-sec--half">
          <div className="auto-rd-sechead">
            <h5 className="auto-rd-h">Language order</h5>
            <span className="auto-rd-hint">Global · empty = alphabetical · {languagesInMap.length} in mapping</span>
          </div>
          <ul className="auto-rd-list auto-rd-list--inline">
            {draft.langOrder.map((l, i) => (
              <li key={l} className="auto-rd-lang">
                <span>{l}</span>
                {!readOnly && (
                  <>
                    <button className="auto-rd-ic" disabled={i === 0}
                      onClick={() => setLangOrder(move(draft.langOrder, i, -1))}><ArrowUp size={12} /></button>
                    <button className="auto-rd-ic" disabled={i === draft.langOrder.length - 1}
                      onClick={() => setLangOrder(move(draft.langOrder, i, 1))}><ArrowDown size={12} /></button>
                    <button className="auto-rd-ic" onClick={() => removeLang(l)}><X size={12} /></button>
                  </>
                )}
              </li>
            ))}
          </ul>
          {!readOnly && (
            <div className="auto-rd-add">
              <span>Add:</span>
              {languagesInMap.filter((l) => !draft.langOrder.includes(l)).map((l) => (
                <button key={l} className="auto-rd-chip" onClick={() => addLang(l)}>
                  <Plus size={11} /> {l}
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="auto-rd-sec auto-rd-sec--half">
          <div className="auto-rd-sechead">
            <h5 className="auto-rd-h">Summary rows</h5>
            <span className="auto-rd-hint">Global · applies to every table</span>
          </div>
          <div className="auto-rd-toggles">
            {TOP_ROWS.map(([k, lbl]) => (
              <label key={k} className="auto-eb-toggle">
                <input type="checkbox" disabled={readOnly} checked={draft.rows[k] !== false}
                  onChange={(e) => setDraft((d) => ({ ...d, rows: { ...d.rows, [k]: e.target.checked } }))} />
                <span>{lbl}</span>
              </label>
            ))}
          </div>
        </section>
      </div>

      {!readOnly && (
        <div className="auto-rd-footer">
          <button className="auto-eb-btn" onClick={handleReset} disabled={saving}>Reset to one table</button>
          <button className="auto-eb-btn auto-eb-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? <RefreshCw size={13} className="auto-eb-spin" /> : <Save size={13} />} Save report design
          </button>
        </div>
      )}
    </div>
  );
}
